import type {
    AppDescriptionContext,
    ComponentBuilder,
    EditedColumnsAndTables,
    InteractiveComponentConfiguratorContext,
    PropertyDescriptor,
    PropertyDescriptorCase,
    RewritingComponentConfiguratorContext,
    SchemaInspector,
} from "..";
import type { SerializedComponent } from "../../../common/components/SerializedApp";
import type { TableName } from "../../../common/database-strings";
import type {
    ColumnType,
    Description,
    InputOutputTables,
    MutatingScreenKind,
    PropertyDescription,
    PropertyKind,
    SourceColumn,
    TableColumn,
    TableGlideType,
} from "../../../common/description";
import { assert, defined } from "../../../common/Support";
import type { ElementBuilder } from "../../serialized-component";

export interface CompatibilityHelper extends SchemaInspector {
    readonly screenName: string | undefined;
    readonly oldComponentConfiguratorContext: AppDescriptionContext;
    findNewColumn(table: TableGlideType, columnName: string): TableColumn | undefined;
    addOtherIssue(issue: string): void;
}

export enum PopulationMode {
    Rewrite,
    RewriteWithNewTable,
    Default,
    UserRequest,
}

export function isRewritePopulationMode(m: PopulationMode): boolean {
    return m === PopulationMode.Rewrite || m === PopulationMode.RewriteWithNewTable;
}

export interface SearchedColumn {
    readonly tableName?: TableName;
    readonly columnName?: string;
}

export interface DescriptionHandler<TDescriptorCase extends PropertyDescriptorCase> {
    readonly kind: PropertyKind;

    defaultUpdateForPropertyCase(
        descr: PropertyDescriptor,
        rootdesc: Description,
        desc: Description,
        pdc: TDescriptorCase,
        avoidDirectColumns: Set<TableColumn>,
        avoidEditedColumns: Set<TableColumn>,
        avoidIndirectColumns: Set<TableColumn>,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        ccc: AppDescriptionContext,
        populationMode: PopulationMode,
        getDefaultCaption: () => string | undefined
    ): Partial<Description> | undefined;

    getEditedColumns(
        pdc: TDescriptorCase,
        pd: PropertyDescription,
        rootdesc: Description,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        ccc: AppDescriptionContext,
        withActions: boolean
    ): EditedColumnsAndTables;

    rewriteWithCase(
        _descr: PropertyDescriptor,
        _pdc: TDescriptorCase,
        _pd: PropertyDescription,
        _newRootdesc: Description,
        newdesc: Description,
        _tables: InputOutputTables,
        _mutatingScreenKind: MutatingScreenKind | undefined,
        _ignoreRequired: boolean,
        _ccc: RewritingComponentConfiguratorContext
    ): Partial<Description> | undefined;

    duplicate(
        descr: PropertyDescriptor,
        pdc: TDescriptorCase,
        propertyDescription: PropertyDescription,
        containingDescription: Description,
        rootDesc: Description,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        iccc: InteractiveComponentConfiguratorContext,
        screensCreated: Set<string>
    ): Promise<Partial<Description>>;

    makeValueForPropertyDescription<X, U>(
        _b: ElementBuilder<X>,
        pd: PropertyDescription,
        undefinedIfEmpty: boolean,
        targetType: ColumnType | undefined,
        _makeGetColumn: (sourceColumn: SourceColumn) => X | U
    ): X | U | undefined;

    getColumnAssignmentName(pd: PropertyDescription): string;
    getColumnAssignmentIcon(pd: PropertyDescription): string;
    getColumnAssignmentLabel(schemaInspector: SchemaInspector, pd: PropertyDescription, table: TableGlideType): string;
    isSearchedColumn(
        searched: SearchedColumn,
        schemaInspector: SchemaInspector,
        pd: PropertyDescription,
        table: TableGlideType
    ): boolean;

    makeArgumentForComponent(
        _builder: ComponentBuilder,
        _table: TableGlideType,
        _pd: PropertyDescription,
        p: TDescriptorCase
    ): SerializedComponent | undefined;

    checkCompatibility(
        _propertyDescription: PropertyDescription,
        _desc: Description,
        _propertyDescriptor: PropertyDescriptor,
        _oldTables: InputOutputTables,
        _helper: CompatibilityHelper
    ): void;
}

const handlers: Map<PropertyKind, DescriptionHandler<any>> = new Map();

export function registerDescriptionHandler<TDescriptorCase extends PropertyDescriptorCase = PropertyDescriptorCase>(
    handler: DescriptionHandler<TDescriptorCase>
): void {
    const { kind } = handler;
    assert(!handlers.has(kind));
    handlers.set(kind, handler);
}

export function handlerForPropertyKind<TDescriptorCase extends PropertyDescriptorCase = PropertyDescriptorCase>(
    kind: PropertyKind
): DescriptionHandler<TDescriptorCase> {
    return defined(handlers.get(kind));
}
